/**
 * Audit Service
 *
 * API client for audit log operations
 */

import { get } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  AuditLog,
  AuditLogFilters,
  AuditLogSearchResult,
  SecurityAlertsResult,
  AuditAction,
  AuditStatus,
  AuditResourceType,
} from '@/domain/audit'

// =============================================================================
// API Functions
// =============================================================================

/**
 * Search/list audit logs with filters
 */
export async function getAuditLogs(filters?: AuditLogFilters): Promise<AuditLogSearchResult> {
  const params = new URLSearchParams()

  if (filters?.userId) params.append('userId', filters.userId)
  if (filters?.action) params.append('action', filters.action)
  if (filters?.resourceType) params.append('resourceType', filters.resourceType)
  if (filters?.resourceId) params.append('resourceId', filters.resourceId)
  if (filters?.status) params.append('status', filters.status)
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom)
  if (filters?.dateTo) params.append('dateTo', filters.dateTo)
  if (filters?.ipAddress) params.append('ipAddress', filters.ipAddress)
  if (filters?.limit) params.append('limit', filters.limit.toString())
  if (filters?.offset) params.append('offset', filters.offset.toString())

  const queryString = params.toString()
  const url = queryString ? `${API_ROUTES.AUDIT.BASE}/logs?${queryString}` : `${API_ROUTES.AUDIT.BASE}/logs`

  return get<AuditLogSearchResult>(url)
}

/**
 * Get audit history for a specific resource
 */
export async function getResourceHistory(
  resourceType: AuditResourceType,
  resourceId: string
): Promise<{ resourceType: string; resourceId: string; history: AuditLog[] }> {
  return get(`/audit/resources/${resourceType}/${resourceId}`)
}

/**
 * Get activity history for a specific user
 */
export async function getUserActivity(
  userId: string
): Promise<{ userId: string; activity: AuditLog[] }> {
  return get(`/audit/users/${userId}/activity`)
}

/**
 * Get security alerts (failed attempts)
 */
export async function getSecurityAlerts(
  options?: { ipAddress?: string; dateFrom?: string; limit?: number }
): Promise<SecurityAlertsResult> {
  const params = new URLSearchParams()

  if (options?.ipAddress) params.append('ipAddress', options.ipAddress)
  if (options?.dateFrom) params.append('dateFrom', options.dateFrom)
  if (options?.limit) params.append('limit', options.limit.toString())

  const queryString = params.toString()
  const url = queryString
    ? `${API_ROUTES.AUDIT.BASE}/security/alerts?${queryString}`
    : `${API_ROUTES.AUDIT.BASE}/security/alerts`

  return get<SecurityAlertsResult>(url)
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format audit timestamp for display
 */
export function formatAuditTimestamp(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * Get action label
 */
export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  CREATE: 'Crear',
  READ: 'Leer',
  UPDATE: 'Actualizar',
  DELETE: 'Eliminar',
  LOGIN: 'Iniciar sesión',
  LOGOUT: 'Cerrar sesión',
  LOGIN_FAILED: 'Login fallido',
  ACCESS_DENIED: 'Acceso denegado',
  DOWNLOAD: 'Descargar',
  UPLOAD: 'Subir',
  EXPORT: 'Exportar',
  ASSIGN: 'Asignar',
  RETURN: 'Devolver',
  SIGN: 'Firmar',
  AMEND: 'Enmendar',
}

/**
 * Get resource type label
 */
export const AUDIT_RESOURCE_LABELS: Record<AuditResourceType, string> = {
  user: 'Usuario',
  patient: 'Paciente',
  study: 'Estudio',
  report: 'Reporte',
  role: 'Rol',
  permission: 'Permiso',
  system: 'Sistema',
  auth: 'Autenticación',
  worklist: 'Worklist',
}

/**
 * Get status badge variant
 */
export function getAuditStatusVariant(
  status: AuditStatus
): 'default' | 'success' | 'destructive' {
  switch (status) {
    case 'SUCCESS':
      return 'success'
    case 'FAILURE':
    case 'DENIED':
      return 'destructive'
    default:
      return 'default'
  }
}

/**
 * Get action icon color
 */
export function getActionColor(action: AuditAction): string {
  switch (action) {
    case 'CREATE':
      return 'text-green-600'
    case 'READ':
      return 'text-blue-600'
    case 'UPDATE':
      return 'text-yellow-600'
    case 'DELETE':
      return 'text-red-600'
    case 'LOGIN':
    case 'LOGOUT':
      return 'text-purple-600'
    case 'LOGIN_FAILED':
    case 'ACCESS_DENIED':
      return 'text-red-600'
    case 'DOWNLOAD':
    case 'UPLOAD':
    case 'EXPORT':
      return 'text-blue-600'
    case 'ASSIGN':
      return 'text-indigo-600'
    case 'RETURN':
      return 'text-orange-600'
    case 'SIGN':
      return 'text-emerald-600'
    case 'AMEND':
      return 'text-amber-600'
    default:
      return 'text-gray-600'
  }
}
